import {
  sendConvertSubTaskToMainTask,
  sendConvertTaskToSubTask,
} from "@/models/tasks.model";
import { AppContext } from "@/services/AppContext/appContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shadcn/components/ui/alert-dialog";
import { Button } from "@/shadcn/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import { SearchTaskBasic } from "@/types";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils";
import { ChevronsUpDown, GitBranch } from "lucide-react";
import React, { useContext, useState } from "react";

type ConvertTaskDialogProps = {
  taskID: number;
  subTask: boolean;
};

const ConvertTaskDialog: React.FC<ConvertTaskDialogProps> = ({
  taskID,
  subTask,
}) => {
  const { precacheTasks, getFullTask, initialGetUserTasks, getPrecacheTasks } =
    useContext(AppContext);
  const [newMainTask, setNewMainTask] = useState<SearchTaskBasic | null>(null);
  const [popOverOpen, setPopOverOpen] = useState(false);

  async function handleConvertTaskToMain() {
    console.log("Convert to main task", taskID);

    const response = await sendConvertSubTaskToMainTask({
      taskID: taskID,
    });

    if (response.rStatus === "success") {
      console.log("Task converted to main task");
      genericSuccessToast("Task converted to main task successfully");
      await getFullTask({ taskID: taskID });
      initialGetUserTasks({ force: true });
      getPrecacheTasks(false, true);
    } else {
      console.error(
        `Error converting task to main task. Error: ${response.message}`
      );
      genericFailureToast(
        `Error converting task to main task. Please try again. Error: ${response.message}`
      );
    }
  }

  async function handleConvertTaskToSub() {
    console.log("Convert to subtask", taskID);
    if (!newMainTask) {
      console.error("No main task selected");
      return;
    }
    console.log("Convert to subtask", taskID, newMainTask.id);

    const response = await sendConvertTaskToSubTask({
      taskID: taskID,
      newMainTaskID: newMainTask.id,
    });

    if (response.rStatus === "success") {
      console.log("Task converted to subtask");
      genericSuccessToast("Task converted to subtask successfully");
      await getFullTask({ taskID: taskID });
      initialGetUserTasks({ force: true });
      getPrecacheTasks(false, true);
    } else {
      console.error(`Error converting task. Error: ${response.message}`);
      genericFailureToast(`Error converting task: ${response.message}`);
    }
  }

  return (
    <AlertDialog>
      <AlertDialogDescription className="hidden">
        Convert task
      </AlertDialogDescription>
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="btn btn-primary rounded-sm"
              >
                <GitBranch />
              </Button>
            </AlertDialogTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <div className="">
              Convert to {subTask ? "main task" : "subtask"}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
        </AlertDialogHeader>
        {subTask ? (
          "This will remove the task from the parent task and make it a standalone task."
        ) : (
          <div className="w-full overflow-hidden">
            <div className="mb-2">
              Please select a main task to add this subtask to:
            </div>
            <Popover modal open={popOverOpen} onOpenChange={setPopOverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full flex justify-start text-left font-normal"
                >
                  <div className="truncate">
                    {newMainTask ? newMainTask.title : "Select main task"}
                  </div>
                  <ChevronsUpDown className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 ">
                <Command className="">
                  <CommandInput placeholder="Search main task..." />
                  <CommandList>
                    <ScrollArea className="h-[200px]">
                      <CommandEmpty>No precache tasks</CommandEmpty>
                      <CommandGroup>
                        {precacheTasks &&
                          [...precacheTasks]
                            .filter((task) => task.id !== taskID)
                            .sort((a, b) => a.title.localeCompare(b.title))
                            .map((task) => (
                              <CommandItem
                                key={task.id}
                                onSelect={() => {
                                  setNewMainTask(task);
                                  setPopOverOpen(false);
                                }}
                              >
                                {task.title}
                              </CommandItem>
                            ))}
                      </CommandGroup>
                    </ScrollArea>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              setNewMainTask(null);
            }}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={async () => {
              if (subTask) {
                await handleConvertTaskToMain();
              } else {
                await handleConvertTaskToSub();
              }
            }}
            disabled={!newMainTask && !subTask}
          >
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConvertTaskDialog;
